import {
  X,
  Send,
  Globe,
  Tag,
  Settings,
  UsersRound,
  Zap,
  // Brain,
  Calendar,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { UserPatient } from '../../services/api/user/userApi';
import { EducationalContent, AssignmentConfig, ContentType } from './educationTypes';
import { useAssignContentToPatients } from '../../services/query/education/education';
import { useGetConditions } from '../../services/query/user/user';
import { AssignContent } from '../../services/api/education/educationApi';
import { showToast } from '../../lib/utils/toast';
import { langs } from '../../enums';

interface Props {
  content: EducationalContent;
  patients: UserPatient[];
  assignmentConfig: AssignmentConfig;
  onConfigChange: (config: AssignmentConfig) => void;
  getTypeIcon: (type: ContentType) => JSX.Element;
  onClose: () => void;
}

// function getAISuggestedTime() {
//   const now = new Date();
//   const suggestedDate = new Date(now);
//   suggestedDate.setDate(now.getDate() + 1);
//   suggestedDate.setHours(10, 0, 0, 0);
//   return {
//     date: suggestedDate.toISOString().split('T')[0],
//     time: '10:00',
//     reason: 'AI suggests tomorrow at 10 AM based on highest open rates',
//   };
// }

export default function AssignmentModal({
  content,
  patients,
  assignmentConfig,
  onConfigChange,
  getTypeIcon,
  onClose,
}: Props) {
  const getFilteredPatients = () => {
    const { targetType, targetCriteria } = assignmentConfig;
    if (targetType === 'all') return patients;
    if (targetType === 'language' && targetCriteria.length > 0)
      return patients.filter((p) => targetCriteria.includes(p.language ?? ''));
    if (targetType === 'condition' && targetCriteria.length > 0)
      return patients.filter((p) =>
        (p.medical_history ?? []).some((cond) =>
          targetCriteria.some((c) => cond.toLowerCase().includes(c.toLowerCase()))
        )
      );
    if (targetType === 'custom')
      return patients.filter((p) => assignmentConfig.selectedPatients.includes(String(p.id)));
    return patients;
  };

  const filteredPatients = getFilteredPatients();

  // Build the API payload from the current config
  const audienceTypeMap: Record<AssignmentConfig['targetType'], AssignContent['audience_type']> = {
    all: 'all_patients',
    language: 'by_language',
    condition: 'by_diagnosis',
    custom: 'custom_selection',
  };

  const assignPayload: AssignContent = {
    content_id: String(content.id),
    audience_type: audienceTypeMap[assignmentConfig.targetType],
    ...(assignmentConfig.targetType === 'language' && assignmentConfig.targetCriteria.length > 0
      ? { language_filter: assignmentConfig.targetCriteria.join(',') }
      : {}),
    ...(assignmentConfig.targetType === 'condition' && assignmentConfig.targetCriteria.length > 0
      ? { diagnosis_filter: assignmentConfig.targetCriteria.join(',') }
      : {}),
    ...(assignmentConfig.targetType === 'custom'
      ? { user_ids: assignmentConfig.selectedPatients }
      : {}),
    schedule_type: assignmentConfig.scheduleType === 'immediate' ? 'send_now' : 'custom_schedule',
    ...(assignmentConfig.scheduleType === 'scheduled' &&
    assignmentConfig.scheduledDate &&
    assignmentConfig.scheduledTime
      ? { schedule_datetime: `${assignmentConfig.scheduledDate}T${assignmentConfig.scheduledTime}:00` }
      : {}),
  };

  const { mutateAsync, isPending } = useAssignContentToPatients(assignPayload);
  const { data: conditionsList = [], isLoading: isLoadingConditions } = useGetConditions({ limit: 50 });

  const handleAssign = async () => {
    const promise = mutateAsync();
    showToast.promise(promise, {
      loading: 'Assigning content...',
      success: 'Content assigned successfully',
      error: 'Failed to assign content',
    });
    promise.then(() => onClose()).catch(() => {});
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-100 rounded-lg text-green-600">
              {getTypeIcon(content.type)}
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">Assign Educational Content</h3>
              <p className="text-sm text-gray-600">{content.title}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Step 1: Target Audience */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">1</div>
              <h4 className="text-lg font-bold text-gray-900">Select Target Audience</h4>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {[
                { type: 'all' as const, icon: UsersRound, label: 'All Patients', desc: 'Send to entire patient database', sub: `${patients.length} patients` },
                { type: 'language' as const, icon: Globe, label: 'By Language', desc: 'Filter by preferred language', sub: 'English or Spanish speakers' },
                { type: 'condition' as const, icon: Tag, label: 'By Condition', desc: 'Patients with specific conditions', sub: 'e.g., diabetes, gum disease' },
                { type: 'custom' as const, icon: Settings, label: 'Custom Selection', desc: 'Manually select patients', sub: 'Individual selection' },
              ].map(({ type, icon: Icon, label, desc, sub }) => (
                <button
                  key={type}
                  onClick={() => onConfigChange({ ...assignmentConfig, targetType: type, targetCriteria: [] })}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    assignmentConfig.targetType === type ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-green-300'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <Icon className={`w-6 h-6 ${assignmentConfig.targetType === type ? 'text-green-600' : 'text-gray-400'}`} />
                    <div>
                      <p className="font-semibold text-gray-900">{label}</p>
                      <p className="text-sm text-gray-600">{desc}</p>
                      <p className="text-xs text-gray-500 mt-1">{sub}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Language Filter */}
            {assignmentConfig.targetType === 'language' && (
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-700 mb-3">Select Languages:</p>
                <div className="flex flex-wrap gap-2">
                  {langs.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        const newCriteria = assignmentConfig.targetCriteria.includes(lang.code)
                          ? assignmentConfig.targetCriteria.filter((l) => l !== lang.code)
                          : [...assignmentConfig.targetCriteria, lang.code];
                        onConfigChange({ ...assignmentConfig, targetCriteria: newCriteria });
                      }}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        assignmentConfig.targetCriteria.includes(lang.code)
                          ? 'bg-green-600 text-white'
                          : 'bg-white text-gray-700 border border-gray-300 hover:border-green-300'
                      }`}
                    >
                      {`${lang.code} ${lang.name}`}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-600 mt-3">{filteredPatients.length} patients match this criteria</p>
              </div>
            )}

            {/* Condition Filter */}
            {assignmentConfig.targetType === 'condition' && (
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-700 mb-3">Select Conditions:</p>
                {isLoadingConditions ? (
                  <p className="text-sm text-gray-500">Loading conditions...</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {conditionsList.map(({ condition }) => (
                      <button
                        key={condition}
                        onClick={() => {
                          const newCriteria = assignmentConfig.targetCriteria.includes(condition)
                            ? assignmentConfig.targetCriteria.filter((c) => c !== condition)
                            : [...assignmentConfig.targetCriteria, condition];
                          onConfigChange({ ...assignmentConfig, targetCriteria: newCriteria });
                        }}
                        className={`px-4 py-2 rounded-lg font-medium capitalize transition-colors ${
                          assignmentConfig.targetCriteria.includes(condition)
                            ? 'bg-green-600 text-white'
                            : 'bg-white text-gray-700 border border-gray-300 hover:border-green-300'
                        }`}
                      >
                        {condition}
                      </button>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-600 mt-3">{filteredPatients.length} patients match this criteria</p>
              </div>
            )}

            {/* Custom Selection */}
            {assignmentConfig.targetType === 'custom' && (
              <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
                <p className="text-sm font-medium text-gray-700 mb-3">Select Patients:</p>
                <div className="space-y-2">
                  {patients.map((patient) => (
                    <label key={patient.id} className="flex items-center space-x-3 p-2 rounded hover:bg-white cursor-pointer">
                      <input
                        type="checkbox"
                        checked={assignmentConfig.selectedPatients.includes(String(patient.id))}
                        onChange={(e) => {
                          const patientId = String(patient.id);
                          const newSelected = e.target.checked
                            ? [...assignmentConfig.selectedPatients, patientId]
                            : assignmentConfig.selectedPatients.filter((id) => id !== patientId);
                          onConfigChange({ ...assignmentConfig, selectedPatients: newSelected });
                        }}
                        className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{patient.first_name} {patient.last_name}</p>
                        <p className="text-xs text-gray-600">{patient.email}</p>
                      </div>
                      <Globe className={`w-4 h-4 ${patient.language === 'es' ? 'text-orange-500' : 'text-blue-500'}`} />
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-600 mt-3">{assignmentConfig.selectedPatients.length} patients selected</p>
              </div>
            )}
          </div>

          {/* Step 2: Schedule Delivery */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">2</div>
              <h4 className="text-lg font-bold text-gray-900">Schedule Email Delivery</h4>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                onClick={() => onConfigChange({ ...assignmentConfig, scheduleType: 'immediate' })}
                className={`p-4 border-2 rounded-lg text-left transition-all ${
                  assignmentConfig.scheduleType === 'immediate' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-green-300'
                }`}
              >
                <div className="flex items-center space-x-3 mb-2">
                  <Zap className={`w-6 h-6 ${assignmentConfig.scheduleType === 'immediate' ? 'text-green-600' : 'text-gray-400'}`} />
                  <p className="font-semibold text-gray-900">Send Now</p>
                </div>
                <p className="text-sm text-gray-600">Immediate delivery to patients</p>
              </button>
{/* 
              <button
                onClick={() => {
                  const ai = getAISuggestedTime();
                  onConfigChange({ ...assignmentConfig, scheduleType: 'ai-optimized', scheduledDate: ai.date, scheduledTime: ai.time });
                }}
                className={`p-4 border-2 rounded-lg text-left transition-all ${
                  assignmentConfig.scheduleType === 'ai-optimized' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-green-300'
                }`}
              >
                <div className="flex items-center space-x-3 mb-2">
                  <Brain className={`w-6 h-6 ${assignmentConfig.scheduleType === 'ai-optimized' ? 'text-green-600' : 'text-gray-400'}`} />
                  <p className="font-semibold text-gray-900">AI Optimized</p>
                </div>
                <p className="text-sm text-gray-600">Best time for engagement</p>
                {assignmentConfig.scheduleType === 'ai-optimized' && (
                  <div className="mt-2 text-xs text-green-700 bg-green-100 rounded px-2 py-1">
                    {getAISuggestedTime().reason}
                  </div>
                )}
              </button> */}

              <button
                onClick={() => onConfigChange({ ...assignmentConfig, scheduleType: 'scheduled' })}
                className={`p-4 border-2 rounded-lg text-left transition-all ${
                  assignmentConfig.scheduleType === 'scheduled' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-green-300'
                }`}
              >
                <div className="flex items-center space-x-3 mb-2">
                  <Calendar className={`w-6 h-6 ${assignmentConfig.scheduleType === 'scheduled' ? 'text-green-600' : 'text-gray-400'}`} />
                  <p className="font-semibold text-gray-900">Custom Schedule</p>
                </div>
                <p className="text-sm text-gray-600">Choose specific date & time</p>
              </button>
            </div>

            {assignmentConfig.scheduleType === 'scheduled' && (
              <div className="mt-4 bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                    <input
                      type="date"
                      value={assignmentConfig.scheduledDate || ''}
                      onChange={(e) => onConfigChange({ ...assignmentConfig, scheduledDate: e.target.value })}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Time</label>
                    <input
                      type="time"
                      value={assignmentConfig.scheduledTime || ''}
                      onChange={(e) => onConfigChange({ ...assignmentConfig, scheduledTime: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Summary */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-900">
                <p className="font-semibold mb-2">Assignment Summary</p>
                <ul className="space-y-1">
                  <li>• Content: <span className="font-medium">{content.title}</span></li>
                  <li>• Recipients: <span className="font-medium">{filteredPatients.length} patients</span></li>
                  <li>• Delivery: <span className="font-medium">
                    {assignmentConfig.scheduleType === 'immediate' && 'Immediate'}
                    {assignmentConfig.scheduleType === 'ai-optimized' && `${assignmentConfig.scheduledDate} at ${assignmentConfig.scheduledTime} (AI Optimized)`}
                    {assignmentConfig.scheduleType === 'scheduled' && assignmentConfig.scheduledDate && assignmentConfig.scheduledTime && `${assignmentConfig.scheduledDate} at ${assignmentConfig.scheduledTime}`}
                    {assignmentConfig.scheduleType === 'scheduled' && (!assignmentConfig.scheduledDate || !assignmentConfig.scheduledTime) && 'Please select date and time'}
                  </span></li>
                  <li>• Method: <span className="font-medium">Email via Reminder System</span></li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
            Cancel
          </button>
          <button
            onClick={handleAssign}
            disabled={
              isPending ||
              (assignmentConfig.targetType === 'all' && patients.length === 0) ||
              (assignmentConfig.targetType === 'language' && assignmentConfig.targetCriteria.length === 0) ||
              (assignmentConfig.targetType === 'condition' && assignmentConfig.targetCriteria.length === 0) ||
              (assignmentConfig.targetType === 'custom' && assignmentConfig.selectedPatients.length === 0) ||
              (assignmentConfig.scheduleType === 'scheduled' && (!assignmentConfig.scheduledDate || !assignmentConfig.scheduledTime))
            }
            className="px-6 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
            <span>{isPending ? 'Assigning...' : 'Confirm & Schedule'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
